import { RefObject, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertErrorSolidIcon,
  AlertInfoSolidIcon,
  ArrowLeftSolidIcon,
  CloseSolidIcon,
  EditOutlineIcon,
  TrashSolidIcon,
} from '@pmi/dsm-react/dist/components/icons';
import {
  Alert,
  Button,
  ChipMetadata,
  LinkButton,
} from '@pmi/dsm-react';
import Overlay from '../Overlay';
import { useNavigate, useParams } from 'react-router';
import { Accordion, Spinner } from 'react-bootstrap';
import {
  CircleInfoIcon,
  PlusIcon,
} from '@pmi/dsm-react/dist/components/icons/16';
import { CheckmarkIcon } from '@pmi/dsm-react/dist/components/icons/avatar';
import { useDispatch } from 'react-redux';
import styles from './cart.module.scss';
import {
  useGetPaymentLinkMutation,
  useRemoveProductMutation,
} from './apiSlice';
import formatCurrency from '../../utils/formatCurrency';
import { BundleOptions, CartDetailsResponse, CartItem } from './models';
import { Product, ProductBundleOption } from '../../../Hub/TrainingHub/models';
import BillingAddress from './BillingAddress';
import { Address, PhoneNumber } from '../../models/ProfileModel';
import { SelectOption } from '../PhoneNumberForm/models';
import formatCartProductType from '../../utils/formatCartProductType';
import useBreakpoint, { SIZE_SM, SIZE_XS } from '../../hooks/useBreakpoint';
import { useAddToCartMutation } from '../../../Hub/TrainingHub/EnrollmentCard/apiSlice';
import Contact from './Contact';
import formatPhoneNumber from '../../utils/formatPhoneNumber';
import getCurrencySymbol from '../../utils/getCurrencySymbol';
import {
  MembershipSku01,
  MembershipSku02,
  ProductTypes,
} from '../../constants';
import ChangeChapter from './ChangeChapter';
import { useGetLocalChaptersQuery } from '../../../Hub/TrainingHub/EnrollmentCard/ProductModal/apiSlice';
import emptySliceApi from '../../store/emptyApiSlice';
import { toastWithAnalytics } from '../../analytics';
import { indiaErrorHandler } from '../../utils/indiaErrorHandler';

type CartProps = {
  isOpen: boolean;
  onClose: () => void;
  cartDetails: CartDetailsResponse;
  userProducts: Product[];
  accountName: string;
  productsLoading: boolean;
  showPhoneNumber: boolean;
  isCartProductsLoading: boolean;
};

interface ExtendedCartItem extends CartItem<ProductBundleOption> {
  dealIds?: string[];
  bundleOptions: ProductBundleOption[];
}

export default function Cart({
  isOpen,
  onClose,
  cartDetails,
  userProducts,
  accountName,
  productsLoading,
  showPhoneNumber,
  isCartProductsLoading,
}: CartProps) {
  const cartRef = useRef<HTMLDivElement | null>(null);
  const indiaErrorRef = useRef<HTMLDivElement | null>(null);
  const noAddressErrorRef = useRef<HTMLDivElement | null>(null);
  const missingAddressInformationErrorRef = useRef<HTMLDivElement | null>(null);
  const noContactErrorRef = useRef<HTMLDivElement | null>(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const bp = useBreakpoint();
  const { accountId } = useParams<{ accountId: string }>();
  const addresses = cartDetails?.addresses;
  const [toRemoveItem, setToRemoveItem] = useState<ExtendedCartItem | null>(
    null,
  );
  const [toRemoveId, setToRemoveId] = useState(null);
  const [billingAddress, setBillingAddress] = useState<Address | null>(null);
  const [editBillingAddress, setEditBillingAddress] = useState(false);
  const [hasNoAddress, setHasNoAddress] = useState(false);
  const billingAccordionItemRef = useRef(null);
  const [phoneNumber, setPhoneNumber] = useState<PhoneNumber | null>(null);
  const [phoneNumberCountries, setPhoneNumberCountries] = useState<
    SelectOption[] | null
  >(null);
  const [editPhoneNumber, setEditPhoneNumber] = useState(false);
  const phoneNumberAccordionItemRef = useRef(null);
  const [showChangeChapterModal, setShowChangeChapterModal] =
    useState<boolean>(false);
  const [selectedProduct, setSelectedProduct] = useState<Product>(null);
  const [chapterSelected, setChapterSelected] = useState<BundleOptions>();
  const [showIndiaError, setShowIndiaError] = useState(null);
  const [cartAddressError, setCartAddressError] = useState<boolean>(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isAddMembershipTriggered, setIsAddMembershipTriggered] =
    useState<boolean>(false);

  const [geoLocationLoading, setGeoLocationLoading] = useState(false);
  const [geoLocation, setGeoLocation] = useState({
    latitude: null,
    longitude: null,
    distance: null,
  });

  const {
    data: chapterList,
    isLoading: chapterListLoading,
    isError: chapterListError,
  } = useGetLocalChaptersQuery(geoLocation, {
    skip: !geoLocation || geoLocationLoading || geoLocation.longitude === null,
  });

  const isModalReady =
    chapterList &&
    !geoLocationLoading &&
    !chapterListLoading &&
    !chapterListError;

  const [removeProduct, removingProduct] = useRemoveProductMutation();
  const [getPaymentLink] = useGetPaymentLinkMutation();
  const [addToCart] = useAddToCartMutation();
  const [onCheckout, setOnCheckout] = useState<boolean>(false);
  const isMobile = bp === SIZE_SM || bp === SIZE_XS;
  const disableBtn = removingProduct.isLoading || onCheckout;
  const disableCheckoutBtn =
    removingProduct.isLoading ||
    onCheckout ||
    isProcessing ||
    hasNoAddress ||
    cartAddressError;

  const handleCloseCart = () => {
    setToRemoveItem(null);
    onClose();
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (cartRef.current && !cartRef.current.contains(event.target as Node)) {
        handleCloseCart();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isModalReady) {
      handleCloseCart();
    }
  }, [isModalReady]);

  const hasProducts = cartDetails?.items?.length > 0;

  const GetGeoLocation = () => {
    setGeoLocationLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setGeoLocation({ latitude, longitude, distance: 100 });
        setGeoLocationLoading(false);
      },
      () => setGeoLocationLoading(false),
    );
  };
  const handleChangeChapter = (
    chapter: BundleOptions,
    item: CartItem,
    matchedProduct: Product,
  ) => {
    setShowChangeChapterModal(true);
    GetGeoLocation();
    setChapterSelected(chapter);
    setSelectedProduct(matchedProduct);
    setToRemoveId(item.id);
  };

  const handleRemoveProduct = async (
    item: CartItem,
    matchedProduct: Product,
  ) => {
    // Extract dealIds and bundleOptions
    const dealIds = matchedProduct?.dealIds;
    const bundleOptions = matchedProduct?.bundleOptions;
    // Enrich CartItem and set it
    setToRemoveItem({
      ...item,
      dealIds,
      bundleOptions,
    });

    try {
      await removeProduct({ accountId, itemId: item.id })
        .unwrap()
        .then(() => {
          // invalidate only when it's called inside the cart
          dispatch(
            emptySliceApi.util.invalidateTags([
              { type: 'CartProducts', id: accountId },
              { type: 'Product' },
            ]),
          );
        });
    } catch (error) {
      console.error('Failed to remove product:', error);
      toastWithAnalytics('Failed to remove product', 'danger');
    }
  };

  const handleUndoRemoveProduct = async () => {
    try {
      const { sku, dealIds, grandTotal, bundleOptions } = toRemoveItem;
      await addToCart({
        accountId,
        items: [
          {
            qty: 1,
            sku,
            dealCode: dealIds[0],
            price: grandTotal,
            bundleOptions: bundleOptions?.map((option) => ({
              optionId: option.id,
              selectionId: parseInt(option.productLinks[0]?.id, 10),
            })),
          },
        ],
      })
        .unwrap()
        .then(() => {
          setToRemoveItem(null);
        });
    } catch (error) {
      console.error('Failed to parse error:', error);
      toastWithAnalytics('Failed to undo product', 'danger');
    }
  };

  const getFirstErrorRef = (): RefObject<HTMLElement> | null => {
    // Check for missing contact information (highest priority)
    const isPhoneNumberRequired =
      showPhoneNumber && !editPhoneNumber && !phoneNumber;

    if (isPhoneNumberRequired) {
      return noContactErrorRef;
    }

    // Check for missing billing address
    if (hasNoAddress) {
      return noAddressErrorRef;
    }

    // Check for incomplete address information
    if (cartAddressError) {
      return missingAddressInformationErrorRef;
    }

    // Check for India-specific payment errors
    if (showIndiaError) {
      return indiaErrorRef;
    }

    // No errors found
    return null;
  };

  const scrollToErrorRef = (ref: RefObject<HTMLElement> | null) => {
    if (ref?.current) {
      setTimeout(() => {
        ref.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }, 250);
    }
  };

  const handleCheckout = () => {
    // check for errors
    const errorRef = getFirstErrorRef();

    if (errorRef) {
      scrollToErrorRef(errorRef);
      return;
    }

    // proceed on checkout if no errors
    setOnCheckout(true);
    getPaymentLink({
      accountId,
    })
      .unwrap()
      .then((response) => {
        window.location.assign(response.paymentLink);
      })
      .catch((err) => {
        setOnCheckout(false);
        const { errorMessage, isIndiaError } = indiaErrorHandler(err);

        if (isIndiaError && errorMessage) {
          setShowIndiaError(errorMessage); // UI shows alert
          return;
        }
        toastWithAnalytics(errorMessage || 'Failed to checkout', 'danger');
      });
  };

  const renderUndoAlert = () => (
    <div className={`${styles.undoAlert} d-flex gap-2 mb-2 items-start`}>
      <AlertInfoSolidIcon
        color="info"
        fontSize="sm"
        className="mx-2 align-items-start"
      />
      <div>
        <div className="typography16 gray600 fw-bold">{toRemoveItem?.name}</div>
        <button
          onClick={handleUndoRemoveProduct}
          type="button"
          className="typography16 gray600 text-decoration-underline cursor-pointer"
          id="btn-cart-undo-change"
        >
          Undo
        </button>
      </div>
      <div className="ms-auto">
        <CloseSolidIcon
          className="cursor-pointer"
          onClick={() => setToRemoveItem(null)}
        />
      </div>
    </div>
  );

  const pMIMembershipProduct = useMemo(() => {
    const memb01 = userProducts.find(
      (p) => p.type === ProductTypes.membership && p.sku === MembershipSku01,
    );
    const memb02 = userProducts.find(
      (p) => p.type === ProductTypes.membership && p.sku === MembershipSku02,
    );
    return memb01 ?? memb02 ?? null;
  }, [cartDetails, userProducts]);

  useEffect(() => {
    if (
      userProducts.length > 0 &&
      !productsLoading &&
      isAddMembershipTriggered &&
      pMIMembershipProduct
    ) {
      // Introduce a slight delay to allow the browser to paint the DOM
      // elements that were created when productsLoading went to false.
      setTimeout(() => {
        const btn = document.querySelectorAll(
          `[data-type="membership"][data-sku]`,
        );

        if (btn && btn.length > 0) {
          handleCloseCart();
          (
            btn[0]?.querySelector('a[role="button"]') as HTMLAnchorElement
          )?.click();
        } else {
          console.warn('Button not found even after product data load.');
        }

        // This must run inside the setTimeout callback to ensure the flag
        // is reset only after the action has been attempted.
        setIsAddMembershipTriggered(false);
      }, 50); // A small delay (e.g., 50ms) is often sufficient
    }
  }, [
    userProducts,
    productsLoading,
    isAddMembershipTriggered,
    pMIMembershipProduct,
  ]);

  const displayBanner = () =>
    hasProducts &&
    pMIMembershipProduct &&
    !userProducts.find(
      (p) => p.type === ProductTypes.membership && p.addToMyProducts,
    ) &&
    userProducts.filter((p) => p.displayMemberSavings && p.memberSavings > 0)
      .length > 0;

  const getCartSavings = () => {
    const savings = cartDetails?.items.map(
      (cp) =>
        userProducts.find((up) => up.sku === cp.sku && up.displayMemberSavings)
          ?.memberSavings ?? 0,
    );
    return savings?.length > 0 ? savings.reduce((a, c) => a + c, 0) : 0;
  };

  const addToCartMemberShipProduct = () => {
    setIsAddMembershipTriggered(true);

    if (window.location.pathname.includes('settings')) {
      // navigate to product list page
      navigate(`/hub/${accountId}`);
    }
  };

  return (
    <>
      {showChangeChapterModal && isModalReady && (
        <ChangeChapter
          localChapters={chapterList}
          isLoading={productsLoading}
          product={selectedProduct}
          defaultChapter={chapterSelected}
          onCancel={() => setShowChangeChapterModal(false)}
          toRemoveId={toRemoveId}
        />
      )}
      <div
        className={`${styles.cartContainer} ${isOpen ? styles.open : ''}`}
        id="cart-block"
      >
        <div className={styles.cart} ref={cartRef}>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h3>Your Shopping Cart</h3>
            <CloseSolidIcon
              className="gray600 cursor-pointer"
              onClick={handleCloseCart}
              fontSize="md"
            />
          </div>
          {!productsLoading && (
            <div className="typography16 gray600 mb-4">
              {hasProducts
                ? `${cartDetails.items.length} Item${
                    cartDetails.items.length > 1 ? 's' : ''
                  } in Cart`
                : 'No Items in Cart'}
            </div>
          )}
          {!productsLoading && displayBanner && getCartSavings() > 0 && (
            <div className={`${styles.cartSaveBanner} mb-3`}>
              <div className="d-flex align-content-center align-items-center">
                <div className="d-block flex-grow-1">
                  <h3>
                    Save {userProducts[0].currencySymbol}
                    {getCartSavings().toFixed(2)} on your order by becoming a
                    member today!
                  </h3>
                </div>
                <div>
                  <Button
                    titleText="Become a Member"
                    enforceTitleCase={false}
                    variant="outline-secondary"
                    className={styles.noTextTransform}
                    onClick={addToCartMemberShipProduct}
                  />
                </div>
              </div>
            </div>
          )}
          {hasProducts && !productsLoading && (
            <Accordion
              defaultActiveKey={[]}
              alwaysOpen
              className={`${styles.cartUserInfo} mb-3`}
              id="accordion-address-phone"
            >
              {showPhoneNumber && (
                <Accordion.Item
                  eventKey="0"
                  className={styles.cartUserInfoBlock}
                  ref={phoneNumberAccordionItemRef}
                  id="item-phone-block"
                >
                  <Accordion.Header
                    className={styles.cartUserInfoHeaderText}
                    onClick={() => setEditPhoneNumber(!editPhoneNumber)}
                  >
                    <span>Contact</span>
                    {phoneNumber && (
                      <LinkButton
                        titleText="Edit Contact"
                        size="sm"
                        className={`${styles.cartUserInfoHeaderEditButton} fw-normal text-dark text-decoration-none`}
                      />
                    )}
                  </Accordion.Header>
                  <Accordion.Body>
                    <Contact
                      onPhoneNumberSelected={(
                        phone: PhoneNumber,
                        countries: SelectOption[],
                        init,
                      ) => {
                        setPhoneNumberCountries(countries);
                        setPhoneNumber(phone);
                        if (!init) {
                          const div: Element =
                            phoneNumberAccordionItemRef?.current;
                          div.querySelector('button').click();
                        }
                        setEditPhoneNumber(false);
                      }}
                      onClose={() => {
                        setEditPhoneNumber(false);
                        const div: Element =
                          phoneNumberAccordionItemRef?.current;
                        div.querySelector('button').click();
                      }}
                    />
                  </Accordion.Body>
                  {!editPhoneNumber && phoneNumber && phoneNumberCountries && (
                    <div className="d-block p-3">
                      <div className="d-block">
                        <p>
                          <strong>Phone Number</strong>
                        </p>
                      </div>
                      <div className="d-block">
                        <p>
                          {formatPhoneNumber(phoneNumber, phoneNumberCountries)}
                        </p>
                      </div>
                    </div>
                  )}
                  {!editPhoneNumber && !phoneNumber && (
                    <div className="d-block p-3" ref={noContactErrorRef}>
                      <div className="d-block mb-3">
                        <div className={styles.customNeutralAlert}>
                          <div className="icon">
                            <CircleInfoIcon />
                          </div>
                          <div>
                            <strong>Phone Number Needed</strong>
                            <p>Add a phone number to proceed with checkout.</p>
                          </div>
                        </div>
                      </div>
                      <Button
                        titleText="Add Phone Number"
                        icon={PlusIcon}
                        type="button"
                        variant="primary"
                        size="sm"
                        onClick={() => {
                          setEditPhoneNumber(true);
                          const div: Element =
                            phoneNumberAccordionItemRef?.current;
                          div.querySelector('button').click();
                        }}
                      />
                    </div>
                  )}
                </Accordion.Item>
              )}
              <Accordion.Item
                eventKey="1"
                className={styles.cartUserInfoBlock}
                ref={billingAccordionItemRef}
                id="item-address-block"
              >
                <Accordion.Header
                  className={styles.cartUserInfoHeaderText}
                  onClick={() => setEditBillingAddress(!editBillingAddress)}
                >
                  <span>Billing Address</span>
                  <LinkButton
                    titleText="Edit Billing"
                    size="sm"
                    className={`${styles.cartUserInfoHeaderEditButton} fw-normal text-dark text-decoration-none`}
                  />
                </Accordion.Header>
                {!isCartProductsLoading && cartAddressError && (
                  <div
                    className="billingAddressError"
                    ref={missingAddressInformationErrorRef}
                  >
                    <p className="alert alert-danger">
                      <div className="d-flex align-items-start">
                        <AlertErrorSolidIcon fontSize="sm" color="danger" />
                        <div className="px-2">
                          <strong className="d-block">
                            Your address is missing information
                          </strong>
                          <span className="d-block">
                            Please update all required fields.
                          </span>
                        </div>
                      </div>
                    </p>
                    {!editBillingAddress && (
                      <Button
                        titleText="Update Billing Address"
                        icon={EditOutlineIcon}
                        size="sm"
                        onClick={() => {
                          const div: Element = billingAccordionItemRef?.current;
                          div?.querySelector('button').click();
                          setEditBillingAddress(true);
                        }}
                      />
                    )}
                  </div>
                )}
                {showIndiaError && (
                  <div ref={indiaErrorRef}>
                    <Alert
                      variant="danger"
                      alertText={showIndiaError}
                      icon={AlertErrorSolidIcon}
                    />
                  </div>
                )}
                <Accordion.Body>
                  <BillingAddress
                    isProcessing={isProcessing}
                    setIsProcessing={setIsProcessing}
                    setCartAddressError={setCartAddressError}
                    onBillingAddressSelected={(address: Address, init) => {
                      setBillingAddress(address);
                      if (!init) {
                        const div: Element = billingAccordionItemRef?.current;
                        div?.querySelector('button').click();
                        setEditBillingAddress(false);
                      }
                    }}
                    setShowIndiaError={setShowIndiaError}
                    isCartProductsLoading={isCartProductsLoading}
                    cartBillingAddress={
                      addresses?.billingAddress?.country
                        ? addresses?.billingAddress
                        : null
                    }
                    onNoAddressAvailable={(noAddress) => {
                      setHasNoAddress(noAddress);
                    }}
                  />
                </Accordion.Body>
                {hasNoAddress && !editBillingAddress && (
                  <div ref={noAddressErrorRef} className="d-block p-3">
                    <div className="d-block mb-3">
                      <div className={styles.customNeutralAlert}>
                        <div className="icon">
                          <CircleInfoIcon />
                        </div>
                        <div>
                          <strong>Billing Address Needed</strong>
                          <p>Add a billing address to proceed with checkout.</p>
                        </div>
                      </div>
                    </div>
                    <div className="d-block mb-3">
                      <Button
                        titleText="Add Billing Address"
                        icon={PlusIcon}
                        size="sm"
                        onClick={() => {
                          const div: Element = billingAccordionItemRef?.current;
                          div?.querySelector('button').click();
                          setEditBillingAddress(true);
                        }}
                      />
                    </div>
                  </div>
                )}
                {!editBillingAddress &&
                  billingAddress &&
                  billingAddress?.address1 && (
                    <div className="d-block p-3">
                      <div className="d-block">
                        <p className="address">{billingAddress?.address1}</p>
                        {billingAddress?.city &&
                          billingAddress?.state &&
                          billingAddress?.postalCode && (
                            <p className="state">
                              {billingAddress?.city},{' '}
                              {billingAddress?.stateLabel}{' '}
                              {billingAddress?.postalCode}
                            </p>
                          )}
                        <p className="country">{billingAddress?.country}</p>
                      </div>
                    </div>
                  )}
              </Accordion.Item>
            </Accordion>
          )}
          <div className={`${styles.divider} mb-4`} />
          {toRemoveItem &&
            !removingProduct.isLoading &&
            !isCartProductsLoading &&
            !productsLoading &&
            !chapterSelected &&
            renderUndoAlert()}
          {hasProducts && !productsLoading ? (
            <div className="cart-items" id="cart-items">
              {cartDetails.items.map((product) => {
                const hasDiscount = product?.discountTotal > 0;
                const isRemoving = toRemoveItem?.id === product.id;
                const matchedProduct = userProducts.find(
                  (p) => p.sku === product.sku,
                );
                const productType = formatCartProductType(matchedProduct?.type);
                const itemPrice = product.grandTotal - product.taxTotal;
                const chapter = product.bundleOptions?.find(
                  (v) => v.isChapterMembership,
                );
                const chapterBundleOption = matchedProduct?.bundleOptions?.find(
                  (bo) => bo.title?.toLowerCase().includes('chapter'),
                );
                const hasMultipleChapters =
                  (chapterBundleOption?.productLinks?.length ?? 0) > 1;
                // Selectable bundle options (e.g. language) — show
                // "<selectOption>: <selectionName>" for each option whose
                // matched product bundle option declares a selectOption.
                const selectedBundleOptions = (
                  product.bundleOptions ?? []
                ).flatMap((bo) => {
                  if (bo.isChapterMembership) return [];
                  const matchedOption = matchedProduct?.bundleOptions?.find(
                    (po) => po.id === bo.optionId,
                  );
                  if (!matchedOption?.selectOption) return [];
                  const matchedProductLinks = matchedOption.productLinks?.find(
                    (pl) => pl.sku === bo.selectionSku,
                  );
                  return [
                    {
                      key: `${product.id}-${bo.optionId}`,
                      label: matchedProductLinks?.label ?? '',
                      value: matchedProductLinks?.value ?? '',
                    },
                  ];
                });

                return (
                  <div
                    key={product.id}
                    className={`mb-4 ${
                      isRemoving ? styles.removingProduct : ''
                    } cart-item`}
                  >
                    <div className="d-flex justify-content-between gap-4">
                      <div>
                        <div className="typography20 gray800 fw-bold mb-1">
                          {product.name}
                        </div>
                        <div className="typography16 gray800">
                          {productType}
                        </div>
                        {selectedBundleOptions.length > 0 && (
                          <div className="typography16 gray600 my-2">
                            {selectedBundleOptions.map((opt) => (
                              <div key={opt.key}>
                                {opt.label}: {opt.value}
                              </div>
                            ))}
                          </div>
                        )}
                        {chapter && (
                          <>
                            <div className="typography16 gray600 my-2">
                              {chapter.selectionName}
                            </div>
                            <div
                              className="d-block d-md-flex align-items-center gap-3"
                              id="cart-change-chapter"
                            >
                              {hasMultipleChapters && (
                                <LinkButton
                                  titleText="Change Chapter"
                                  variant="light"
                                  onClick={() =>
                                    handleChangeChapter(
                                      chapter,
                                      product,
                                      matchedProduct,
                                    )
                                  }
                                />
                              )}
                              <ChipMetadata
                                text="Chapter Membership Included"
                                icon={<CheckmarkIcon />}
                                variant="violet"
                              />
                            </div>
                          </>
                        )}
                      </div>
                      <div className="text-end">
                        {hasDiscount && (
                          <div
                            className="typography18 gray600 mb-2 text-decoration-line-through price-original"
                            id={`price-original-${matchedProduct.sku}`}
                          >
                            {matchedProduct?.currencySymbol}
                            {formatCurrency(product.basePrice)}
                          </div>
                        )}
                        <div
                          className="typography20 gray800 fw-bold mb-2 price-now"
                          id={`price-now-${matchedProduct.sku}`}
                        >
                          {matchedProduct?.currencySymbol}
                          {formatCurrency(itemPrice)}
                        </div>
                        {hasDiscount && (
                          <div
                            className={`typography16 price-discount mb-2 ${styles.savePriceText}`}
                            id={`price-discount-${matchedProduct.sku}`}
                          >
                            You save {matchedProduct?.currencySymbol}
                            {formatCurrency(product.discountTotal)}
                          </div>
                        )}
                        <span id={`product-remove-${matchedProduct.sku}`}>
                          <LinkButton
                            className={styles.removeContainer}
                            onClick={() =>
                              handleRemoveProduct(product, matchedProduct)
                            }
                            icon={TrashSolidIcon}
                            iconAlign="left"
                            titleText="Remove"
                            disabled={disableBtn}
                          />
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
              <div className={`${styles.divider} mb-4`} />
            </div>
          ) : (
            <div>
              {!productsLoading && (
                <div
                  className={`${styles.emptyCartContainer} d-flex justify-content-center align-items-center mb-4`}
                >
                  <div id="cart-no-products">
                    <div className="text-center mb-4 typography16 gray600">
                      Your cart is empty
                    </div>
                    <Button
                      titleText="Add Products"
                      onClick={handleCloseCart}
                      variant="outline-secondary"
                      size="sm"
                      className={styles.checkoutBtn}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
          {(isCartProductsLoading || productsLoading) && (
            <div className="text-center mb-4 typography16 gray600">
              <Spinner size="sm" /> Loading...
            </div>
          )}
          {!productsLoading && (
            <>
              <div className="mb-4">
                <div className="d-flex justify-content-between mb-2">
                  <div className="typography16 gray700 text-nowrap">
                    Subtotal
                  </div>
                  <div className="typography16 gray700 text-end">
                    {getCurrencySymbol(cartDetails?.cart.currencyCode)}
                    {formatCurrency(
                      (cartDetails?.cart.subtotal ?? 0) +
                        (cartDetails?.cart.memberDiscountTotal ?? 0),
                    )}
                  </div>
                </div>
                {cartDetails?.cart?.memberDiscountTotal > 0 && (
                  <div className="d-flex justify-content-between mb-2">
                    <div className="typography16 gray800">
                      PMI Member Discount
                    </div>
                    <div
                      className={`typography16 gray600 text-end ${styles.discountPriceText}`}
                    >
                      -{getCurrencySymbol(cartDetails?.cart.currencyCode)}
                      {formatCurrency(cartDetails?.cart?.memberDiscountTotal)}
                    </div>
                  </div>
                )}
                {cartDetails?.cart?.companyDiscountTotal > 0 && (
                  <div className="d-flex justify-content-between mb-2">
                    <div className="typography16 gray800">
                      {accountName} Discount
                    </div>
                    <div
                      className={`typography16 gray600 text-end ${styles.discountPriceText}`}
                    >
                      -{getCurrencySymbol(cartDetails?.cart.currencyCode)}
                      {formatCurrency(cartDetails?.cart?.companyDiscountTotal)}
                    </div>
                  </div>
                )}
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div className="typography16 gray700 text-nowrap">
                    Estimated Tax
                  </div>
                  <div className="typography16 gray700 text-end">
                    {getCurrencySymbol(cartDetails?.cart.currencyCode)}
                    {formatCurrency(cartDetails?.cart.taxTotal)}
                  </div>
                </div>

                <div className={`${styles.divider} my-2`} />
                <div className="d-flex justify-content-between gap-4">
                  <div
                    className={`typography${
                      isMobile ? 16 : 18
                    } gray900 fw-bold`}
                  >
                    Estimated Order Total
                  </div>
                  <div
                    className={`typography${
                      isMobile ? 16 : 20
                    } gray900 fw-bold text-end`}
                  >
                    {cartDetails?.cart?.currencyCode}
                    {getCurrencySymbol(cartDetails?.cart.currencyCode)}
                    {formatCurrency(cartDetails?.cart?.grandTotal)}
                  </div>
                </div>
              </div>

              {hasProducts && (
                <div
                  className="d-flex flex-column flex-sm-row justify-content-between gap-2 my-3"
                  id="cart-buttons"
                >
                  <span id="cart-checkout-btn" className="order-1 order-sm-2 ">
                    <Button
                      titleText="Checkout"
                      variant="primary"
                      size="sm"
                      fullWidth={isMobile}
                      className={`${styles.checkoutBtn} mb-2 mb-sm-0`}
                      onClick={handleCheckout}
                      disabled={disableCheckoutBtn}
                    />
                  </span>
                  <span className="order-2 order-sm-1" id="cart-add-more-btn">
                    <Button
                      titleText="Add more products"
                      variant="light"
                      fullWidth={isMobile}
                      icon={ArrowLeftSolidIcon}
                      iconAlign="left"
                      size="sm"
                      onClick={handleCloseCart}
                      disabled={disableBtn}
                    />
                  </span>
                </div>
              )}
            </>
          )}
        </div>
        <Overlay
          variant="PMIOverlay"
          global
          animated
          fade
          iconColor="brand-dark"
          overlayColor="black"
          overlayOpacity={1}
          show={onCheckout}
        />
      </div>
    </>
  );
}
